import React, { useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Sheet,
  Tab,
  TabList,
  Tabs,
  Typography,
} from '@mui/joy';
import { SettingsOutlined, ArrowBack } from '@mui/icons-material';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AdminOnboardingAPI } from '../../../lib/apiAdmin';
import { AdminOrganizationsDashboard } from '../AdminOrganizationsDashboard';
import { AdminTaskQueue } from '../onboarding/AdminTaskQueue';
import { AdminSequencesPanel } from '../onboarding/AdminSequencesPanel';
import { AdminNpsPanel } from '../onboarding/AdminNpsPanel';
import { CXSettingsPanel } from '../onboarding/CXSettingsPanel';
import { EmailBrandingPanel } from './EmailBrandingPanel';
import { TeamSignaturePanel } from './TeamSignaturePanel';
import { OrgDetailDrawer } from '../OrgDetailDrawer';

const tools = [
  [
    'onboarding',
    'Onboarding playbook',
    'Steps, welcome emails, assignment and booking links',
  ],
  [
    'sequences',
    'Email sequences',
    'Automated follow-up campaigns and templates',
  ],
  ['nps', 'Customer surveys', 'NPS campaigns, responses and follow-up'],
  ['branding', 'Email branding', 'Logo, accent color and footer for admin emails'],
  ['signature', 'Team signature', 'A consistent sign-off for personal emails'],
];
export function AdminAccountsPanel() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const section = location.pathname.split('/')[3] || '';
  const settings = section === 'settings';
  const tool = location.pathname.split('/')[4] || 'onboarding';
  const customerId = params.get('customer');
  const orgId = params.get('organization');
  const customer = useQuery({
    queryKey: ['admin', 'customer', customerId],
    queryFn: () => AdminOnboardingAPI.getCustomer(customerId!),
    enabled: !!customerId && !orgId,
  });
  useEffect(() => {
    if (customer.data && !orgId && customerId)
      setParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          next.set('organization', customer.data.organization);
          next.delete('customer');
          return next;
        },
        { replace: true },
      );
  }, [customer.data, orgId, customerId, setParams]);
  const close = () =>
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        ['organization', 'customer', 'profileTab'].forEach((key) =>
          next.delete(key),
        );
        return next;
      },
      { replace: true },
    );
  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 2,
          alignItems: 'start',
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography level="h3">
            {settings ? 'Accounts settings' : 'Accounts'}
          </Typography>
          <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {settings
              ? 'Configure the shared tools your Customer Success team uses.'
              : 'One place to understand each customer, see the next step, and follow through.'}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startDecorator={settings ? <ArrowBack /> : <SettingsOutlined />}
          onClick={() =>
            navigate(
              settings ? '/manage/accounts' : '/manage/accounts/settings',
            )
          }
        >
          {settings ? 'Back to accounts' : 'Accounts settings'}
        </Button>
      </Box>
      {!settings && (
        <Tabs
          value={section === 'tasks' ? 'tasks' : 'accounts'}
          onChange={(_, value) =>
            navigate(
              value === 'tasks' ? '/manage/accounts/tasks' : '/manage/accounts',
            )
          }
          sx={{ bgcolor: 'transparent' }}
        >
          <TabList aria-label="Accounts workspace">
            <Tab value="accounts">All accounts</Tab>
            <Tab value="tasks">Team tasks</Tab>
          </TabList>
        </Tabs>
      )}
      {settings ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '230px minmax(0,1fr)' },
            gap: 3,
          }}
        >
          <Sheet
            variant="outlined"
            component="nav"
            aria-label="Accounts settings"
            sx={{
              p: 1,
              borderRadius: 'lg',
              alignSelf: 'start',
              display: 'grid',
              gap: 0.5,
            }}
          >
            {tools.map(([key, title, description]) => (
              <Button
                key={key}
                variant={tool === key ? 'soft' : 'plain'}
                color={tool === key ? 'primary' : 'neutral'}
                aria-current={tool === key ? 'page' : undefined}
                onClick={() => navigate(`/manage/accounts/settings/${key}`)}
                sx={{ display: 'block', textAlign: 'left', p: 1.25 }}
              >
                <Typography level="title-sm">{title}</Typography>
                <Typography level="body-xs" sx={{ mt: 0.5 }}>
                  {description}
                </Typography>
              </Button>
            ))}
          </Sheet>
          <Box sx={{ minWidth: 0 }}>
            {tool === 'sequences' ? (
              <AdminSequencesPanel />
            ) : tool === 'nps' ? (
              <AdminNpsPanel />
            ) : tool === 'branding' ? (
              <EmailBrandingPanel />
            ) : tool === 'signature' ? (
              <TeamSignaturePanel />
            ) : (
              <CXSettingsPanel />
            )}
          </Box>
        </Box>
      ) : section === 'tasks' ? (
        <AdminTaskQueue onOpenCustomer={(id) => setParams({ customer: id })} />
      ) : (
        <AdminOrganizationsDashboard />
      )}
      {customer.isError && (
        <Alert
          color="danger"
          endDecorator={
            <Button onClick={() => customer.refetch()}>Retry</Button>
          }
        >
          Could not open this account.
        </Alert>
      )}
      {customerId && customer.isPending && !orgId && (
        <Typography role="status">Opening account…</Typography>
      )}
      <OrgDetailDrawer
        orgId={orgId}
        open={!!orgId}
        initialTab={params.get('profileTab') || 'overview'}
        onClose={close}
        onImpersonate={() =>
          setParams((previous) => {
            const next = new URLSearchParams(previous);
            next.set('profileTab', 'people');
            return next;
          })
        }
        onViewTickets={() => navigate(`/manage/support?organization=${orgId}`)}
      />
    </Box>
  );
}
